import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Truck,
  Fuel,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ChevronRight,
} from "lucide-react";

const MODE_BG = {
  cash: "bg-emerald-100 text-emerald-700",
  imprest: "bg-bravura-100 text-bravura-700",
  qr: "bg-sky-100 text-sky-700",
};
const STATUS_ICON = {
  approved: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
  pending: <AlertCircle className="h-3.5 w-3.5 text-bravura" />,
  rejected: <XCircle className="h-3.5 w-3.5 text-red-600" />,
};

export default function MyRefills() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/fuel/refills", { params: { mine: true, limit: 100 } });
        setRows(data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalSpent = rows
    .filter((r) => r.status === "approved")
    .reduce((s, r) => s + (r.total_amount || 0), 0);
  const totalLitres = rows
    .filter((r) => r.status === "approved")
    .reduce((s, r) => s + (r.diesel_litres || 0), 0);

  return (
    <div className="space-y-4" data-testid="my-refills-page">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Total Spent</div>
          <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Chivo" }}>
            {formatINR(totalSpent)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Diesel Litres</div>
          <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Chivo" }}>
            {totalLitres.toFixed(1)} L
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
          No refills yet.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <Link
            key={r.id}
            to={`/driver/refills/${r.id}`}
            data-testid={`refill-row-${r.id}`}
            className="block rounded-xl bg-white ring-1 ring-slate-200 p-3 active:scale-[0.99] transition"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-bravura-100 flex items-center justify-center flex-shrink-0">
                <Fuel className="h-5 w-5 text-bravura-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-bold text-slate-900 truncate">{r.truck_no}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${MODE_BG[r.payment_mode]}`}>
                    {r.payment_mode.toUpperCase()}
                  </span>
                  {STATUS_ICON[r.status]}
                  {r.tank_full && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      FULL
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {r.refill_date} · {r.diesel_litres} L · {formatINR(r.total_amount)} · {r.station_name || "—"}
                </div>
                {r.mileage_kmpl && (
                  <div className="text-[11px] text-emerald-600 font-semibold mt-0.5">
                    Mileage: {r.mileage_kmpl} km/L
                  </div>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
